/**
 * MonitorPage — health dashboard for the entire estate.
 *
 * Sections (top to bottom):
 *   1. KPI strip — open alerts by severity, sites at risk, aggregate uplink
 *   2. Event history chart (multi-line by severity) + AI estate summary
 *      sharing a card on the left; Site health DataTable on the right
 *   3. Device statuses — searchable/filterable alert list with ack support
 *   4. Network events — per-column searchable event log with severity inference
 *   5. Licenses — per-network license rollup table
 */
import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Download,
  ExternalLink,
  FileImage,
  FileText,
  Info,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  Wifi,
} from "lucide-react";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { KpiCard } from "@/components/ui/kpi-card";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import {
  PanelSkeleton,
  Skeleton,
  TableSkeleton,
} from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { useMonitor } from "@/stores/monitor";
import { useShallow } from "zustand/react/shallow";
import type {
  Alert,
  AlertSeverity,
  NetworkEventRow,
  NetworkLicenseRollup,
  SiteHealth,
} from "@/api/schemas/monitor";
import { useDocumentVisible } from "@/lib/use-document-visible";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";
import { AiExplain } from "@/components/app/ai-explain";
import { MonitorAlertChart } from "@/components/app/monitor/monitor-alert-chart";
import { MonitorAiPanel } from "@/components/app/monitor/monitor-ai-panel";
import { RangePicker } from "@/components/app/dashboard/range-picker";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/* ---------- helpers ---------- */

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "—";
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  return `${Math.floor(ms / 86_400_000)}d ago`;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtCachedAt(iso: string | null): string {
  if (!iso) return "Live fetch";
  return `Cache refreshed ${timeAgo(iso)}`;
}

const EVENT_RANGE_OPTIONS = [
  { id: "1h", label: "Last 1 hour" },
  { id: "24h", label: "Last 24 hours" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
];

function rangeToTimespanSec(rangeId: string): number {
  if (rangeId === "1h") return 60 * 60;
  if (rangeId === "24h") return 24 * 60 * 60;
  if (rangeId === "7d") return 7 * 24 * 60 * 60;
  return 30 * 24 * 60 * 60;
}

function eventRangeLabel(rangeId: string): string {
  return (
    EVENT_RANGE_OPTIONS.find((option) => option.id === rangeId)?.label ??
    "Last 24 hours"
  );
}

function parseEventCustomRange(
  _rangeId: string,
): { from: number; to: number } | null {
  return null;
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

const SEV_RANK: Record<AlertSeverity, number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

function severityStyle(s: AlertSeverity): {
  pill: "ok" | "info" | "warn" | "err" | "neutral";
  label: string;
  icon: React.ReactNode;
} {
  switch (s) {
    case "critical":
      return {
        pill: "err",
        label: "critical",
        icon: <ShieldAlert size={14} />,
      };
    case "warning":
      return {
        pill: "warn",
        label: "warning",
        icon: <AlertTriangle size={14} />,
      };
    case "info":
      return { pill: "info", label: "info", icon: <Info size={14} /> };
  }
}

/** Infer severity from an alertType string (mirrors chart logic). */
function inferEventSeverity(alertType: string): AlertSeverity {
  const t = alertType.toLowerCase();
  if (/offline|unreachable|down|critical|fail|disconnect|hardware/i.test(t))
    return "critical";
  if (/warn|high|latency|retry|degraded|alert|error|congestion/i.test(t))
    return "warning";
  return "info";
}

function getAlertPageNumbers(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages: (number | "…")[] = [0];
  const left = Math.max(1, current - 1);
  const right = Math.min(total - 2, current + 1);
  if (left > 1) pages.push("…");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 2) pages.push("…");
  pages.push(total - 1);
  return pages;
}

/* ============================================ */
/* PAGE                                         */
/* ============================================ */

function useMonitorPage(eventChartExportRef: React.RefObject<HTMLDivElement | null>) {
  const [eventHistoryRange, setEventHistoryRange] = useState("24h");
  const [exportingEventPdf, setExportingEventPdf] = useState(false);
  const [exportingEventImage, setExportingEventImage] = useState(false);
  const [siteNameFilter, setSiteNameFilter] = useState("");
  const [siteRegionFilter, setSiteRegionFilter] = useState("");
  const [siteAvailabilityFilter, setSiteAvailabilityFilter] = useState("");
  const [siteAlertsFilter, setSiteAlertsFilter] = useState("");
  const [siteUplinkFilter, setSiteUplinkFilter] = useState("");
  const [licenseSiteFilter, setLicenseSiteFilter] = useState("");
  const [licenseCountFilter, setLicenseCountFilter] = useState("");
  const [licenseDevicesFilter, setLicenseDevicesFilter] = useState("");
  const [licenseUnlicensedFilter, setLicenseUnlicensedFilter] = useState("");
  const [licenseConditionFilter, setLicenseConditionFilter] = useState("");
  const [licenseVendorFilter, setLicenseVendorFilter] = useState("");

  const {
    alerts,
    alertsHistory,
    sites,
    networkEvents,
    licenses,
    sitesCachedAt,
    alertsCachedAt,
    alertsHistoryCachedAt,
    networkEventsCachedAt,
    licensesCachedAt,
    status,
    sectionStatus,
    error,
    load,
    loadNetworkEvents,
    loadLicenses,
    ack,
  } = useMonitor(
    useShallow((s) => ({
      alerts: s.alerts,
      alertsHistory: s.alertsHistory,
      sites: s.sites,
      networkEvents: s.networkEvents,
      licenses: s.licenses,
      sitesCachedAt: s.sitesCachedAt,
      alertsCachedAt: s.alertsCachedAt,
      alertsHistoryCachedAt: s.alertsHistoryCachedAt,
      networkEventsCachedAt: s.networkEventsCachedAt,
      licensesCachedAt: s.licensesCachedAt,
      status: s.status,
      sectionStatus: s.sectionStatus,
      error: s.error,
      load: s.load,
      loadNetworkEvents: s.loadNetworkEvents,
      loadLicenses: s.loadLicenses,
      ack: s.ack,
    })),
  );

  const visible = useDocumentVisible();
  const eventHistoryTimespanSec = rangeToTimespanSec(eventHistoryRange);

  function handleEventRangeChange(nextRange: string) {
    if (!EVENT_RANGE_OPTIONS.some((option) => option.id === nextRange)) return;
    setEventHistoryRange(nextRange);
  }

  // Load all sections on mount — no longer deferred to tab clicks.
  useEffect(() => {
    void load({ alertsHistoryTimespanSec: eventHistoryTimespanSec });
    void loadNetworkEvents();
    void loadLicenses();
  }, [eventHistoryTimespanSec, load, loadNetworkEvents, loadLicenses]);

  // Foreground-only 30s auto-refresh.
  useEffect(() => {
    if (!visible) return;
    const t = setInterval(
      () => void load({ alertsHistoryTimespanSec: eventHistoryTimespanSec }),
      30_000,
    );
    return () => clearInterval(t);
  }, [eventHistoryTimespanSec, visible, load]);

  async function downloadEventHistoryPdf() {
    if (!eventChartExportRef.current || alertsHistory.length === 0) return;
    setExportingEventPdf(true);
    try {
      const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const canvas = await html2canvas(eventChartExportRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const image = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 28;
      const availableWidth = pageWidth - margin * 2;
      const scale = availableWidth / canvas.width;
      const targetWidth = canvas.width * scale;
      const targetHeight = canvas.height * scale;
      const x = (pageWidth - targetWidth) / 2;
      const y = 88;

      pdf.setFontSize(16);
      pdf.text("Monitor Event History", margin, 30);
      pdf.setFontSize(10);
      pdf.text(`Range: ${eventRangeLabel(eventHistoryRange)}`, margin, 48);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, 62);
      pdf.addImage(image, "PNG", x, y, targetWidth, targetHeight);

      pdf.save(`monitor-event-history-${eventHistoryRange}.pdf`);
    } finally {
      setExportingEventPdf(false);
    }
  }

  async function downloadEventHistoryImage() {
    if (!eventChartExportRef.current || alertsHistory.length === 0) return;
    setExportingEventImage(true);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(eventChartExportRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
      });
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, "image/png");
      });
      if (!blob) return;
      triggerBlobDownload(
        blob,
        `monitor-event-history-${eventHistoryRange}.png`,
      );
    } finally {
      setExportingEventImage(false);
    }
  }

  /* ---------- KPIs ---------- */
  const kpis = useMemo(() => {
    const open = alerts.filter((a) => !a.ackedAt);
    return {
      criticals: open.filter((a) => a.severity === "critical").length,
      warnings: open.filter((a) => a.severity === "warning").length,
      sitesAtRisk: sites.filter(
        (s) => s.alertsActive > 0 || s.devicesOnline < s.devicesTotal,
      ).length,
      uplinkSum: sites.reduce((sum, s) => sum + (s.uplinkMbps ?? 0), 0),
    };
  }, [alerts, sites]);

  /* ---------- Sorted alerts ---------- */
  const sortedAlerts = useMemo(
    () =>
      [...alerts].sort((a, b) => {
        const aOpen = a.ackedAt ? 1 : 0;
        const bOpen = b.ackedAt ? 1 : 0;
        if (aOpen !== bOpen) return aOpen - bOpen;
        return SEV_RANK[a.severity] - SEV_RANK[b.severity];
      }),
    [alerts],
  );

  /* ---------- Site health rows ---------- */
  type SiteHealthRow = SiteHealth & { availabilityPct: number };
  const siteRows = useMemo<SiteHealthRow[]>(
    () =>
      sites
        .map((s) => ({
          ...s,
          availabilityPct:
            s.devicesTotal === 0
              ? 100
              : (s.devicesOnline / s.devicesTotal) * 100,
        }))
        .sort((a, b) => {
          if (b.alertsActive !== a.alertsActive)
            return b.alertsActive - a.alertsActive;
          if (a.availabilityPct !== b.availabilityPct)
            return a.availabilityPct - b.availabilityPct;
          return a.name.localeCompare(b.name);
        }),
    [sites],
  );

  const filteredSiteRows = useMemo(() => {
    const nameQ = siteNameFilter.trim().toLowerCase();
    const regionQ = siteRegionFilter.trim().toLowerCase();
    const availabilityQ = siteAvailabilityFilter.trim().toLowerCase();
    const alertsQ = siteAlertsFilter.trim().toLowerCase();
    const uplinkQ = siteUplinkFilter.trim().toLowerCase();

    return siteRows.filter((row) => {
      const availabilityText = `${row.devicesOnline}/${row.devicesTotal} ${row.availabilityPct.toFixed(0)}%`;
      const uplinkText = `${(row.uplinkMbps ?? 0).toFixed(2)} mbps`;
      if (
        nameQ &&
        !(
          (row.hotelName ?? row.name).toLowerCase().includes(nameQ) ||
          row.name.toLowerCase().includes(nameQ)
        )
      ) {
        return false;
      }
      if (regionQ && !row.region.toLowerCase().includes(regionQ)) return false;
      if (
        availabilityQ &&
        !availabilityText.toLowerCase().includes(availabilityQ)
      ) {
        return false;
      }
      if (alertsQ && !String(row.alertsActive).includes(alertsQ)) return false;
      if (uplinkQ && !uplinkText.includes(uplinkQ)) return false;
      return true;
    });
  }, [
    siteAlertsFilter,
    siteAvailabilityFilter,
    siteNameFilter,
    siteRegionFilter,
    siteRows,
    siteUplinkFilter,
  ]);

  /* ---------- Network events (merged) ---------- */
  const combinedNetworkEvents = useMemo<NetworkEventRow[]>(() => {
    const keyOf = (r: NetworkEventRow) =>
      [
        r.networkId,
        r.occurredAt ?? "",
        r.alertTypeId ?? r.alertType,
        r.deviceSerial ?? "",
      ].join("|");
    const merged = new Map<string, NetworkEventRow>();
    for (const r of alertsHistory) merged.set(keyOf(r), r);
    for (const r of networkEvents) merged.set(keyOf(r), r);
    return Array.from(merged.values()).sort((a, b) => {
      const at = a.occurredAt ?? "";
      const bt = b.occurredAt ?? "";
      return at === bt ? 0 : at > bt ? -1 : 1;
    });
  }, [alertsHistory, networkEvents]);

  const filteredLicenses = useMemo(() => {
    const siteQ = licenseSiteFilter.trim().toLowerCase();
    const countQ = licenseCountFilter.trim().toLowerCase();
    const devicesQ = licenseDevicesFilter.trim().toLowerCase();
    const unlicensedQ = licenseUnlicensedFilter.trim().toLowerCase();
    const conditionQ = licenseConditionFilter.trim().toLowerCase();
    const vendorQ = licenseVendorFilter.trim().toLowerCase();

    return licenses.filter((row) => {
      const conditionText = [
        `${row.goodConditionCount} good`,
        `${row.aboutToExpireCount} expiring`,
        `${row.expiredCount} expired`,
      ]
        .join(" ")
        .toLowerCase();
      const vendorText = (row.vendor ?? "meraki").toLowerCase();
      if (
        siteQ &&
        !(
          row.networkName.toLowerCase().includes(siteQ) ||
          row.networkSlug.toLowerCase().includes(siteQ)
        )
      ) {
        return false;
      }
      if (countQ && !String(row.licenseCount).includes(countQ)) return false;
      if (devicesQ && !String(row.devicesTotal).includes(devicesQ))
        return false;
      if (unlicensedQ && !String(row.unlicensedCount).includes(unlicensedQ)) {
        return false;
      }
      if (conditionQ && !conditionText.includes(conditionQ)) return false;
      if (vendorQ && !vendorText.includes(vendorQ)) return false;
      return true;
    });
  }, [
    licenseConditionFilter,
    licenseCountFilter,
    licenseDevicesFilter,
    licenseSiteFilter,
    licenseUnlicensedFilter,
    licenseVendorFilter,
    licenses,
  ]);

  /* ---------- Column defs (memoised) ---------- */
  const siteColumns = useMemo<DataTableColumn<SiteHealthRow>[]>(
    () => [
      {
        key: "hotelName",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Hotel / site
            </span>
            <Input
              value={siteNameFilter}
              onChange={(e) => setSiteNameFilter(e.target.value)}
              placeholder="Hotel or network"
              className="h-8"
            />
          </div>
        ),
        width: "1.4fr",
        sortable: true,
        sortValue: (r) => r.hotelName ?? r.name,
        render: (r) => (
          <div>
            <div className="text-sm text-fg">{r.hotelName ?? r.name}</div>
            <div className="font-mono text-[10.5px] text-fg-mute">{r.name}</div>
          </div>
        ),
      },
      {
        key: "region",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">Region</span>
            <Input
              value={siteRegionFilter}
              onChange={(e) => setSiteRegionFilter(e.target.value)}
              placeholder="Region contains"
              className="h-8"
            />
          </div>
        ),
        width: "0.6fr",
        sortable: true,
        sortValue: (r) => r.region,
        render: (r) => r.region,
      },
      {
        key: "availabilityPct",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Availability
            </span>
            <Input
              value={siteAvailabilityFilter}
              onChange={(e) => setSiteAvailabilityFilter(e.target.value)}
              placeholder="e.g. 95%"
              className="h-8"
            />
          </div>
        ),
        width: "0.9fr",
        sortable: true,
        sortValue: (r) => r.availabilityPct,
        render: (r) => (
          <div>
            <div className="font-mono text-xs text-fg-dim">
              {r.devicesOnline}/{r.devicesTotal}
            </div>
            <div className="text-xs text-fg-mute">
              {r.availabilityPct.toFixed(0)}% online
            </div>
          </div>
        ),
      },
      {
        key: "alertsActive",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Open alerts
            </span>
            <Input
              value={siteAlertsFilter}
              onChange={(e) => setSiteAlertsFilter(e.target.value)}
              placeholder="Count"
              className="h-8"
            />
          </div>
        ),
        width: "0.7fr",
        align: "right",
        sortable: true,
        sortValue: (r) => r.alertsActive,
        render: (r) =>
          r.alertsActive > 0 ? (
            <Badge variant="err" size="sm">
              {r.alertsActive}
            </Badge>
          ) : (
            <Badge variant="ok" size="sm">
              0
            </Badge>
          ),
      },
      {
        key: "uplinkMbps",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">Uplink</span>
            <Input
              value={siteUplinkFilter}
              onChange={(e) => setSiteUplinkFilter(e.target.value)}
              placeholder="Mbps"
              className="h-8"
            />
          </div>
        ),
        width: "0.7fr",
        align: "right",
        sortable: true,
        sortValue: (r) => r.uplinkMbps ?? 0,
        render: (r) => `${(r.uplinkMbps ?? 0).toFixed(2)} Mbps`,
      },
    ],
    [
      siteAlertsFilter,
      siteAvailabilityFilter,
      siteNameFilter,
      siteRegionFilter,
      siteUplinkFilter,
    ],
  );

  const licenseColumns = useMemo<DataTableColumn<NetworkLicenseRollup>[]>(
    () => [
      {
        key: "networkName",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">Site</span>
            <Input
              value={licenseSiteFilter}
              onChange={(e) => setLicenseSiteFilter(e.target.value)}
              placeholder="Site or slug"
              className="h-8"
            />
          </div>
        ),
        width: "1.2fr",
        sortable: true,
        sortValue: (r) => r.networkName,
        render: (r) => (
          <div>
            <div className="text-sm text-fg">{r.networkName}</div>
            <div className="font-mono text-[10.5px] text-fg-mute">
              {r.networkSlug}
            </div>
          </div>
        ),
      },
      {
        key: "licenseCount",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Licenses
            </span>
            <Input
              value={licenseCountFilter}
              onChange={(e) => setLicenseCountFilter(e.target.value)}
              placeholder="Count"
              className="h-8"
            />
          </div>
        ),
        width: "0.6fr",
        align: "right",
        sortable: true,
        sortValue: (r) => r.licenseCount,
        render: (r) => r.licenseCount,
      },
      {
        key: "devicesTotal",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Devices
            </span>
            <Input
              value={licenseDevicesFilter}
              onChange={(e) => setLicenseDevicesFilter(e.target.value)}
              placeholder="Count"
              className="h-8"
            />
          </div>
        ),
        width: "0.6fr",
        align: "right",
        sortable: true,
        sortValue: (r) => r.devicesTotal,
        render: (r) => r.devicesTotal,
      },
      {
        key: "unlicensedCount",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Unlicensed
            </span>
            <Input
              value={licenseUnlicensedFilter}
              onChange={(e) => setLicenseUnlicensedFilter(e.target.value)}
              placeholder="Count"
              className="h-8"
            />
          </div>
        ),
        width: "0.7fr",
        align: "right",
        sortable: true,
        sortValue: (r) => r.unlicensedCount,
        render: (r) =>
          r.unlicensedCount > 0 ? (
            <Badge variant="warn" size="sm">
              {r.unlicensedCount}
            </Badge>
          ) : (
            <Badge variant="ok" size="sm">
              0
            </Badge>
          ),
      },
      {
        key: "condition",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <div className="flex items-center gap-1">
              <span className="font-mono uppercase tracking-[0.1em]">
                Condition
              </span>
              <span
                className="rounded-full border border-line px-1.5 py-0.5 text-[10px] text-fg-mute"
                title="'About to expire' means < 3 months remaining."
                aria-label="About to expire means the license has less than three months left."
              >
                ?
              </span>
            </div>
            <Input
              value={licenseConditionFilter}
              onChange={(e) => setLicenseConditionFilter(e.target.value)}
              placeholder="good/expired"
              className="h-8"
            />
          </div>
        ),
        width: "1fr",
        sortable: true,
        sortValue: (r) => r.expiredCount * 1000 + r.aboutToExpireCount,
        render: (r) => (
          <div className="flex flex-wrap gap-1">
            <Badge variant="ok" size="sm">
              {r.goodConditionCount} good
            </Badge>
            <Badge variant="warn" size="sm">
              {r.aboutToExpireCount} expiring
            </Badge>
            <Badge variant="err" size="sm">
              {r.expiredCount} expired
            </Badge>
          </div>
        ),
      },
      {
        key: "vendor",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">Vendor</span>
            <Input
              value={licenseVendorFilter}
              onChange={(e) => setLicenseVendorFilter(e.target.value)}
              placeholder="meraki/aruba"
              className="h-8"
            />
          </div>
        ),
        width: "0.5fr",
        render: (r) => (
          <Badge variant={r.vendor === "aruba" ? "info" : "neutral"} size="sm">
            {r.vendor ?? "meraki"}
          </Badge>
        ),
      },
      {
        key: "details",
        label: "",
        width: "0.8fr",
        align: "right",
        render: (r) =>
          r.vendor === "aruba" ? null : (
            <Button asChild size="sm" variant="ghost">
              <Link to={`/sites/${r.networkSlug}/dashboard#licenses`}>
                <ExternalLink size={12} />
                Site licenses
              </Link>
            </Button>
          ),
      },
    ],
    [
      licenseConditionFilter,
      licenseCountFilter,
      licenseDevicesFilter,
      licenseSiteFilter,
      licenseUnlicensedFilter,
      licenseVendorFilter,
    ],
  );

  const isInitialLoading =
    status === "loading" &&
    alerts.length === 0 &&
    alertsHistory.length === 0 &&
    sites.length === 0;
  const allMonitorDataReady =
    (sectionStatus.alerts === "ready" || sectionStatus.alerts === "error") &&
    (sectionStatus.metrics === "ready" || sectionStatus.metrics === "error") &&
    (sectionStatus.sites === "ready" || sectionStatus.sites === "error") &&
    (sectionStatus.networkEvents === "ready" ||
      sectionStatus.networkEvents === "error") &&
    (sectionStatus.licenses === "ready" || sectionStatus.licenses === "error");

  return {
    eventHistoryRange,
    exportingEventPdf, exportingEventImage,
    kpis, sortedAlerts, filteredSiteRows, filteredLicenses,
    siteColumns, licenseColumns, combinedNetworkEvents,
    alerts, alertsHistory, sites, networkEvents, licenses,
    sitesCachedAt, alertsCachedAt, alertsHistoryCachedAt,
    networkEventsCachedAt, licensesCachedAt,
    status, sectionStatus, error,
    load, loadNetworkEvents, loadLicenses, ack,
    handleEventRangeChange, downloadEventHistoryPdf, downloadEventHistoryImage,
    eventHistoryTimespanSec, isInitialLoading, allMonitorDataReady,
  };
}

export function MonitorPage() {
  const animatedRootRef = useUiAutoAnimate();
  const eventChartExportRef = useRef<HTMLDivElement | null>(null);
  const m = useMonitorPage(eventChartExportRef);

  if (m.isInitialLoading) {
    return (
      <div ref={animatedRootRef} className="flex flex-col gap-6">
        <div className="flex items-center gap-2 text-xs text-fg-mute">
          <Skeleton className="h-3 w-20" />
          <span className="opacity-40">/</span>
          <Skeleton className="h-3 w-14" />
        </div>
        <div className="flex items-end justify-between gap-6">
          <div className="flex max-w-[680px] flex-1 flex-col gap-2">
            <Skeleton className="h-10 w-36" />
            <Skeleton className="h-4 w-[92%]" />
            <Skeleton className="h-4 w-[80%]" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-28 rounded-full" />
            <Skeleton className="h-9 w-24 rounded-lg" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2 xl:grid-cols-4">
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
          <PanelSkeleton />
        </div>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.4fr_1fr]">
          <PanelSkeleton className="h-[340px]" />
          <PanelSkeleton className="h-[340px]" />
        </div>
        <PanelSkeleton className="h-[220px]" />
      </div>
    );
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">Operations</Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Monitor</span>
      </div>

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">Monitor</h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Health snapshot of the entire estate — open alerts, recent activity
            volume, and per-site availability
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={m.kpis.criticals > 0 ? "err" : "ok"} size="md">
            {m.kpis.criticals > 0 ? <ShieldAlert size={11} /> : <ShieldCheck size={11} />}
            {m.kpis.criticals > 0 ? `${m.kpis.criticals} critical` : "no criticals"}
          </Badge>
          <Button size="sm" variant="ghost"
            onClick={() => void m.load({ force: true, alertsHistoryTimespanSec: m.eventHistoryTimespanSec })}
            disabled={m.status === "loading"}>
            <RefreshCw size={13} />
            Refresh
          </Button>
        </div>
      </div>

      {m.error ? (
        <output className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach">
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{m.error}</span>
          </span>
        </output>
      ) : null}

      <div className="grid grid-cols-4 gap-3.5">
        <KpiCard label="Critical alerts" value={m.kpis.criticals} icon={<ShieldAlert size={14} />}
          trend={m.kpis.criticals > 0 ? "down" : "flat"} trendLabel={m.kpis.criticals > 0 ? "needs attention" : "clean window"} />
        <KpiCard label="Warning alerts" value={m.kpis.warnings} icon={<AlertTriangle size={14} />}
          trend="flat" trendLabel="open but non-critical" />
        <KpiCard label="Sites at risk" value={m.kpis.sitesAtRisk} icon={<ShieldCheck size={14} />}
          trend={m.kpis.sitesAtRisk > 0 ? "down" : "up"} trendLabel="degraded or alerting" />
        <KpiCard label="Aggregate uplink" value={`${m.kpis.uplinkSum.toFixed(2)} Mbps`}
          icon={<Wifi size={14} />} trend="flat" trendLabel="last 5 min" />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1.4fr_1fr]">
        <div className="flex flex-col gap-5">
          <Card className="flex flex-col">
            <CardContent className="flex flex-1 flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <CardTitle>Event History</CardTitle>
                  <CardSub className="mt-0.5">
                    {eventRangeLabel(m.eventHistoryRange)} · {fmtCachedAt(m.alertsHistoryCachedAt)}
                  </CardSub>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <RangePicker value={m.eventHistoryRange} onChange={m.handleEventRangeChange}
                    options={EVENT_RANGE_OPTIONS} rangeLabel={eventRangeLabel}
                    parseCustomRange={parseEventCustomRange} customRangePrefix="monitor-custom:" />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" disabled={m.alertsHistory.length === 0}>
                        <Download size={12} />
                        Download
                        <ChevronDown size={11} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onSelect={() => void m.downloadEventHistoryPdf()}
                        disabled={m.alertsHistory.length === 0 || m.exportingEventPdf}>
                        <FileText size={12} className="mr-1 text-fg-dim" />
                        {m.exportingEventPdf ? "Generating PDF" : "PDF"}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => void m.downloadEventHistoryImage()}
                        disabled={m.alertsHistory.length === 0 || m.exportingEventImage}>
                        <FileImage size={12} className="mr-1 text-fg-dim" />
                        {m.exportingEventImage ? "Generating image" : "PNG image"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button size="sm" variant="ghost"
                    onClick={() => void m.load({ force: true, alertsHistoryTimespanSec: m.eventHistoryTimespanSec })}
                    disabled={m.sectionStatus.metrics === "loading"}>
                    <RefreshCw size={13} className={cn(m.sectionStatus.metrics === "loading" && "animate-spin")} />
                    Refresh
                  </Button>
                </div>
              </div>
              <div ref={eventChartExportRef}>
                <MonitorAlertChart rows={m.alertsHistory} cachedAt={null} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <MonitorAiPanel
                alerts={m.alerts} sites={m.sites} alertsHistory={m.alertsHistory}
                networkEvents={m.networkEvents} licenses={m.licenses}
                criticals={m.kpis.criticals} warnings={m.kpis.warnings}
                sitesAtRisk={m.kpis.sitesAtRisk} uplinkSum={m.kpis.uplinkSum}
                timeframeLabel={eventRangeLabel(m.eventHistoryRange)}
                allDataReady={m.allMonitorDataReady} className="h-full"
              />
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent>
            <CardTitle>Site health</CardTitle>
            <CardSub className="mb-4">Sortable site rollup. {fmtCachedAt(m.sitesCachedAt)}</CardSub>
            <DataTable columns={m.siteColumns} data={m.filteredSiteRows} rowKey={(r) => r.name}
              pageSize={5} emptyMessage="No sites loaded." />
          </CardContent>
        </Card>
      </div>

      <DeviceStatusesSection
        sortedAlerts={m.sortedAlerts}
        cachedAt={m.alertsCachedAt}
        onRefresh={() => void m.load({ force: true, alertsHistoryTimespanSec: m.eventHistoryTimespanSec })}
        refreshing={m.status === "loading"}
        onAck={(id) => void m.ack(id)}
      />

      <NetworkEventsSection
        events={m.combinedNetworkEvents}
        cachedAt={m.networkEventsCachedAt}
        loading={m.sectionStatus.networkEvents === "loading"}
        onRefresh={() => void m.loadNetworkEvents({ force: true })}
      />

      <LicensesSection
        licenses={m.filteredLicenses}
        cachedAt={m.licensesCachedAt}
        loading={m.sectionStatus.licenses === "loading"}
        onRefresh={() => void m.loadLicenses({ force: true })}
        columns={m.licenseColumns}
      />
    </div>
  );
}

/* ============================================================ */
/* Device Statuses section                                       */
/* ============================================================ */

type AckFilter = "all" | "open" | "acked";

function DeviceStatusesSection({
  sortedAlerts,
  cachedAt,
  onRefresh,
  refreshing,
  onAck,
}: {
  sortedAlerts: Alert[];
  cachedAt: string | null;
  onRefresh: () => void;
  refreshing: boolean;
  onAck: (id: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [ackFilter, setAckFilter] = useState<AckFilter>("all");
  const [alertPage, setAlertPage] = useState(0);
  const ALERTS_PER_PAGE = 15;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return sortedAlerts.filter((a) => {
      if (ackFilter === "open" && a.ackedAt) return false;
      if (ackFilter === "acked" && !a.ackedAt) return false;
      if (!q) return true;
      return (
        a.title.toLowerCase().includes(q) ||
        a.source.toLowerCase().includes(q) ||
        a.target.toLowerCase().includes(q) ||
        a.vendor.toLowerCase().includes(q) ||
        a.severity.toLowerCase().includes(q)
      );
    });
  }, [sortedAlerts, search, ackFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / ALERTS_PER_PAGE));
  const effectivePage = Math.min(alertPage, pageCount - 1);
  const paged = filtered.slice(
    effectivePage * ALERTS_PER_PAGE,
    (effectivePage + 1) * ALERTS_PER_PAGE,
  );

  return (
    <Card>
      {/* Section header */}
      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <CardTitle>Device Statuses</CardTitle>
          <CardSub className="mt-0.5">
            {sortedAlerts.filter((a) => !a.ackedAt).length} open ·{" "}
            {sortedAlerts.filter((a) => a.ackedAt).length} acknowledged ·{" "}
            {fmtCachedAt(cachedAt)}
          </CardSub>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={refreshing}
        >
          <RefreshCw size={13} />
          Refresh
        </Button>
      </div>

      {/* Search + ack filter bar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-line/60 px-5 py-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-mute"
          />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setAlertPage(0); }}
            placeholder="Search title, source, target, vendor…"
            className="pl-8 text-sm"
          />
        </div>
        <div className="flex items-center gap-1 rounded-lg border border-line bg-surface p-0.5">
          {(["all", "open", "acked"] as AckFilter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => { setAckFilter(f); setAlertPage(0); }}
              className={cn(
                "rounded-md px-3 py-1.5 text-xs font-medium transition-colors capitalize",
                ackFilter === f
                  ? "bg-violet/15 text-violet"
                  : "text-fg-mute hover:text-fg",
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Alert list */}
      <div className="px-5 pt-2 pb-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <CheckCircle2 size={20} className="text-mint" />
            <CardTitle>
              {search || ackFilter !== "all"
                ? "No matching alerts"
                : "All clear"}
            </CardTitle>
            <CardSub>
              {search || ackFilter !== "all"
                ? "Try adjusting your filters."
                : "No device status alerts in the current window."}
            </CardSub>
          </div>
        ) : (
          <>
            <ul className="m-0 flex list-none flex-col p-0">
              {paged.map((a) => (
                <MemoAlertRow key={a.id} alert={a} onAck={() => onAck(a.id)} />
              ))}
            </ul>
            {pageCount > 1 && (
              <div className="flex items-center justify-between border-t border-line/60 py-3">
                <span className="text-xs text-fg-mute">
                  {filtered.length} alert{filtered.length !== 1 ? "s" : ""} ·
                  page {effectivePage + 1} of {pageCount}
                </span>
                <div className="flex items-center gap-0.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={effectivePage === 0}
                    onClick={() => setAlertPage(effectivePage - 1)}
                    className="px-2"
                  >
                    ←
                  </Button>
                  {getAlertPageNumbers(effectivePage, pageCount).map((p, i) =>
                    p === "…" ? (
                      <span
                        key={`ell-${i}`}
                        className="w-7 select-none text-center text-xs text-fg-mute"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setAlertPage(p)}
                        className={cn(
                          "h-7 w-7 rounded text-xs font-medium transition-colors",
                          p === effectivePage
                            ? "bg-violet/20 text-violet"
                            : "text-fg-mute hover:bg-surface-tint hover:text-fg",
                        )}
                      >
                        {p + 1}
                      </button>
                    ),
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={effectivePage >= pageCount - 1}
                    onClick={() => setAlertPage(effectivePage + 1)}
                    className="px-2"
                  >
                    →
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
}

/* ============================================================ */
/* Network Events section                                        */
/* ============================================================ */

function NetworkEventsSection({
  events,
  cachedAt,
  loading,
  onRefresh,
}: {
  events: NetworkEventRow[];
  cachedAt: string | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const [occurredFilter, setOccurredFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");

  const networkEventColumns = useMemo<
    DataTableColumn<NetworkEventRow & { _severity: AlertSeverity }>[]
  >(
    () => [
      {
        key: "occurredAt",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Occurred
            </span>
            <Input
              value={occurredFilter}
              onChange={(e) => setOccurredFilter(e.target.value)}
              placeholder="Date contains"
              className="h-8"
            />
          </div>
        ),
        width: "0.9fr",
        sortable: true,
        sortValue: (r) => r.occurredAt ?? "",
        render: (r) => fmtDateTime(r.occurredAt),
      },
      {
        key: "networkName",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">Site</span>
            <Input
              value={siteFilter}
              onChange={(e) => setSiteFilter(e.target.value)}
              placeholder="Site or slug"
              className="h-8"
            />
          </div>
        ),
        width: "1.1fr",
        sortable: true,
        sortValue: (r) => r.networkName,
        render: (r) => (
          <div>
            <div className="text-sm text-fg">{r.networkName}</div>
            <div className="font-mono text-[10.5px] text-fg-mute">
              {r.networkSlug}
            </div>
          </div>
        ),
      },
      {
        key: "alertType",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">
              Event type
            </span>
            <Input
              value={eventTypeFilter}
              onChange={(e) => setEventTypeFilter(e.target.value)}
              placeholder="Type or ID"
              className="h-8"
            />
          </div>
        ),
        width: "1.1fr",
        sortable: true,
        sortValue: (r) => r.alertType,
        render: (r) => (
          <div>
            <div className="flex items-center gap-1.5 text-sm text-fg">
              <SeverityDot severity={r._severity} />
              {r.alertType}
            </div>
            <div className="font-mono text-[10.5px] text-fg-mute">
              {r.alertTypeId ?? "-"}
            </div>
          </div>
        ),
      },
      {
        key: "deviceSerial",
        label: (
          <div className="flex flex-col gap-1 normal-case font-sans text-xs tracking-normal text-fg-mute">
            <span className="font-mono uppercase tracking-[0.1em]">Scope</span>
            <Input
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value)}
              placeholder="Serial contains"
              className="h-8"
            />
          </div>
        ),
        width: "0.9fr",
        render: (r) =>
          r.deviceSerial ? (
            <div className="flex flex-col gap-1">
              <span className="font-mono text-xs text-fg-dim">
                {r.deviceSerial}
              </span>
              <Button asChild size="sm" variant="ghost" className="w-fit px-0">
                <Link
                  to={`/devices?serial=${encodeURIComponent(r.deviceSerial)}`}
                >
                  <ExternalLink size={12} />
                  Device
                </Link>
              </Button>
            </div>
          ) : (
            <Badge variant="neutral" size="sm">
              network-wide
            </Badge>
          ),
      },
    ],
    [eventTypeFilter, occurredFilter, scopeFilter, siteFilter],
  );

  const filteredEvents = useMemo(() => {
    const occurred = occurredFilter.trim().toLowerCase();
    const site = siteFilter.trim().toLowerCase();
    const eventType = eventTypeFilter.trim().toLowerCase();
    const scope = scopeFilter.trim().toLowerCase();
    return events.flatMap((r) => {
      const dateStr = fmtDateTime(r.occurredAt).toLowerCase();
      if (occurred && !dateStr.includes(occurred.toLowerCase())) return [];
      if (
        site &&
        !r.networkName.toLowerCase().includes(site.toLowerCase()) &&
        !r.networkSlug.toLowerCase().includes(site.toLowerCase())
      )
        return [];
      if (
        eventType &&
        !r.alertType.toLowerCase().includes(eventType.toLowerCase()) &&
        !(r.alertTypeId ?? "").toLowerCase().includes(eventType.toLowerCase())
      )
        return [];
      if (
        scope &&
        !(r.deviceSerial ?? "").toLowerCase().includes(scope.toLowerCase())
      )
        return [];
      return [{ ...r, _severity: inferEventSeverity(r.alertType) }];
    });
  }, [eventTypeFilter, events, occurredFilter, scopeFilter, siteFilter]);

  return (
    <Card>
      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <CardTitle>Network Events</CardTitle>
          <CardSub className="mt-0.5">{fmtCachedAt(cachedAt)}</CardSub>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={loading}
        >
          <RefreshCw size={13} />
          Refresh
        </Button>
      </div>
      <div className="px-5 py-4">
        {loading && events.length === 0 ? (
          <TableSkeleton
            columns="0.9fr 1.1fr 1.1fr 0.9fr"
            columnCount={4}
            rows={6}
          />
        ) : (
          <DataTable
            columns={networkEventColumns}
            data={filteredEvents}
            rowKey={(r) =>
              `${r.networkId}-${r.occurredAt ?? "na"}-${r.alertTypeId ?? r.alertType}-${r.deviceSerial ?? "dev"}`
            }
            pageSize={8}
            emptyMessage="No network events match the current filters."
            enableExport
            exportFileName="monitor-network-events"
          />
        )}
      </div>
    </Card>
  );
}

/* ============================================================ */
/* Licenses section                                             */
/* ============================================================ */

function LicensesSection({
  licenses,
  cachedAt,
  loading,
  onRefresh,
  columns,
}: {
  licenses: NetworkLicenseRollup[];
  cachedAt: string | null;
  loading: boolean;
  onRefresh: () => void;
  columns: DataTableColumn<NetworkLicenseRollup>[];
}) {
  return (
    <Card>
      <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
        <div>
          <CardTitle>Licenses</CardTitle>
          <CardSub className="mt-0.5">
            {licenses.length > 0
              ? `${licenses.length} network rollup${licenses.length !== 1 ? "s" : ""} · `
              : ""}
            {fmtCachedAt(cachedAt)}
          </CardSub>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={onRefresh}
          disabled={loading}
        >
          <RefreshCw size={13} />
          Refresh
        </Button>
      </div>
      <div className="px-5 py-4">
        {loading && licenses.length === 0 ? (
          <TableSkeleton
            columns="1.2fr 0.6fr 0.6fr 0.7fr 1fr 0.8fr"
            columnCount={6}
            rows={6}
          />
        ) : (
          <DataTable
            columns={columns}
            data={licenses}
            rowKey={(r) => r.networkId}
            pageSize={10}
            emptyMessage="No license rollups loaded. Click Refresh to fetch."
            enableExport
            exportFileName="monitor-licenses"
          />
        )}
      </div>
    </Card>
  );
}

/* ============================================================ */
/* AlertRow sub-component                                        */
/* ============================================================ */

function AlertRow({ alert, onAck }: { alert: Alert; onAck: () => void }) {
  const sty = severityStyle(alert.severity);
  const isAcked = !!alert.ackedAt;
  const [expanded, setExpanded] = useState(false);

  const hasTarget = alert.target && alert.target !== "(no network)";
  const hasOpenedAt =
    !!alert.openedAt && !Number.isNaN(new Date(alert.openedAt).getTime());

  return (
    <li
      className={cn(
        "border-t border-line/60 first:border-t-0",
        isAcked && "opacity-60",
      )}
    >
      <div className="grid grid-cols-[24px_28px_1fr_auto_auto_auto_72px] items-center gap-3 px-2 py-3">
        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          aria-label={expanded ? "Collapse" : "Expand"}
          className="text-fg-mute hover:text-fg"
        >
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <span
          className={cn(
            "inline-flex h-7 w-7 items-center justify-center rounded-full",
            sty.pill === "err" && "bg-rose/15 text-rose",
            sty.pill === "warn" && "bg-peach/15 text-peach",
            sty.pill === "info" && "bg-violet/15 text-violet",
          )}
        >
          {sty.icon}
        </span>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-fg">
            {alert.title}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10.5px] text-fg-mute">
            <span className="font-mono">{alert.source}</span>
            {hasTarget ? (
              <>
                <span>·</span>
                <span>
                  target <b className="font-mono text-fg-dim">{alert.target}</b>
                </span>
              </>
            ) : null}
            {isAcked && alert.ackedAt ? (
              <>
                <span>·</span>
                <span className="font-mono text-mint">
                  ack {timeAgo(alert.ackedAt)}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <Badge variant="neutral" size="sm">
          {alert.vendor}
        </Badge>
        <Badge variant={sty.pill} size="sm">
          {sty.icon}
          {sty.label}
        </Badge>
        {hasOpenedAt ? (
          <span className="text-right font-mono text-[10.5px] text-fg-mute">
            opened {timeAgo(alert.openedAt)}
          </span>
        ) : (
          <span />
        )}
        <div className="flex justify-end">
          {isAcked ? (
            <Badge variant="ok" size="sm">
              ack'd
            </Badge>
          ) : (
            <div className="group relative">
              <Button size="sm" variant="ghost" onClick={onAck}>
                Ack
              </Button>
              {/* Tooltip */}
              <div
                className={cn(
                  "pointer-events-none absolute bottom-full right-0 z-10 mb-1.5 w-52",
                  "rounded-lg border border-line bg-surface px-3 py-2 text-[11px] text-fg-dim shadow-lg",
                  "opacity-0 transition-opacity group-hover:opacity-100",
                )}
                role="tooltip"
              >
                <b className="text-fg">Acknowledge</b> — dismiss this alert. It
                will be dimmed and filtered to the "Acked" view. The next
                refresh will reconcile with the server.
              </div>
            </div>
          )}
        </div>
      </div>
      {expanded ? (
        <div className="border-t border-line/60 bg-surface-tint/40 px-5 py-3">
          <AiExplain
            kind="alert"
            context={alert as unknown as Record<string, unknown>}
          />
          {alert.detail && Object.keys(alert.detail).length > 0 ? (
            <pre className="mt-2.5 max-h-40 overflow-auto rounded-md border border-line bg-surface px-2.5 py-2 font-mono text-[10.5px] text-fg-dim">
              {JSON.stringify(alert.detail, null, 2)}
            </pre>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

const MemoAlertRow = memo(AlertRow);

/* Small inline component for a severity dot in the network events table. */
function SeverityDot({ severity }: { severity: AlertSeverity }) {
  return (
    <span
      className={cn(
        "inline-block h-1.5 w-1.5 shrink-0 rounded-full",
        severity === "critical" && "bg-rose",
        severity === "warning" && "bg-peach",
        severity === "info" && "bg-violet",
      )}
    />
  );
}
